/**
 * Invoices route — Phase 5b rebuild.
 *
 * What this file owns:
 *   - Page composition (PageHeader + KPIs + Tabs + DataTable)
 *   - Create / edit / detail Sheet orchestration
 *   - Mock-aware mutations: create, save-draft, issue, edit-draft,
 *     record-payment, reverse-payment, void, apply-credit-note
 *   - Print preview routing — clicks on "Print" mount the print view
 *     off-screen, then `printElement` clones it into a hidden iframe
 *     and triggers the browser print dialog.
 *
 * Mock-aware: `useMockData()` decides whether reads/writes hit the local
 * `invoicingMock` store or the real InstantDB query layer (not yet wired here).
 */

import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useAppSettings } from "../../../lib/appSettings";
import {
  nextInvoiceNumber as getNextInvoiceNumber,
  generateNumber,
  DEFAULT_PREFIXES,
} from "../../../lib/invoiceNumbering";
import { createFileRoute } from "@tanstack/react-router";
import { FileText, Pencil, Plus, Printer } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ConfirmAction } from "../../../components/ConfirmAction";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  InvoiceStatusBadge,
  type InvoiceStatus,
} from "../../../components/sales/InvoiceStatusBadge";
import { PaymentMethodBadge } from "../../../components/sales/PaymentMethodBadge";
import { InvoicePrintView } from "../../../components/sales/InvoicePrintView";
import {
  InvoiceForm,
  computeInvoiceTotals,
  type InvoiceFormValues,
  type DealerDetailInfo,
} from "../../../components/forms/InvoiceForm";
import {
  PaymentForm,
  type PaymentFormValues,
} from "../../../components/forms/PaymentForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockInvoices,
  mockInvoiceLines,
  mockPayments,
  mockCreditNotes,
  daysOverdue,
  dealerOutstandingBaseCents,
  type MockInvoice,
  type MockInvoiceLine,
  type MockPayment,
  type MockCreditNote,
} from "../../../lib/invoicingMock";
import { resolvePriceMock } from "../../../lib/pricingMock";
import { mockProducts, type MockProduct } from "../../../lib/catalogMock";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

import {
  mockDealers,
  findDealerTier,
  type MockDealer,
} from "../../../lib/salesMock";
import {
  mockBranches,
  mockStockMovements,
  mockWarehouses,
  type MockStockMovement,
  type MockWarehouse,
} from "../../../lib/inventoryMock";
import { createSalesShipmentMovements } from "../../../lib/stockMovements";

type MockDealerRecord = MockDealer;

const INVOICE_PURPOSE_OPTIONS = [
  { value: "invoice_pdf", label: "Invoice PDF" },
  { value: "delivery_challan", label: "Delivery Challan" },
  { value: "supporting_doc", label: "Supporting Doc" },
  { value: "signed_copy", label: "Signed Copy" },
  { value: "po_from_dealer", label: "Dealer PO" },
];

export const Route = createFileRoute("/_app/sales/invoices")({
  component: InvoicesPage,
});

const NOW = Date.now();

/** Tabs map directly to the invoice status enum + the synthetic "all". */
type TabValue =
  | "all"
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void";

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "partially_paid", label: "Partially paid" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "void", label: "Void" },
];

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function effectiveStatus(inv: MockInvoice): InvoiceStatus {
  if (inv.status === "paid" || inv.status === "void" || inv.status === "draft")
    return inv.status;
  if (inv.dueDate < NOW && inv.balanceDueCents > 0n) return "overdue";
  return inv.status;
}

/* ─────────────────────────────────────────────────────────────
 * Page component
 * ───────────────────────────────────────────────────────────── */

function InvoicesPage() {
  const isMock = useMockData();
  const { companyProfile } = useAppSettings();

  const invoiceConfig = useMemo(
    () => ({
      prefix: companyProfile.invoicePrefix,
      yearFormat: companyProfile.invoiceYearFormat,
      padLength: companyProfile.invoicePadLength,
    }),
    [
      companyProfile.invoicePrefix,
      companyProfile.invoiceYearFormat,
      companyProfile.invoicePadLength,
    ],
  );

  // Working copies — let mutations update them locally without touching the
  // module-scope mock arrays.
  const [rows, setRows] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    isMock ? mockInvoices : [],
  );
  const [lines, setLines] = usePersistentMockState<MockInvoiceLine[]>(
    "invoiceLines",
    isMock ? mockInvoiceLines : [],
  );
  const [payments, setPayments] = usePersistentMockState<MockPayment[]>(
    "payments",
    isMock ? mockPayments : [],
  );
  const [creditNotes] = usePersistentMockState<MockCreditNote[]>(
    "creditNotes",
    mockCreditNotes,
  );
  const [dealers] = usePersistentMockState<MockDealerRecord[]>(
    "dealers",
    mockDealers,
  );
  const [branches] = usePersistentMockState<typeof mockBranches>(
    "branches",
    mockBranches,
  );
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [warehouses] = usePersistentMockState<MockWarehouse[]>(
    "warehouses",
    mockWarehouses,
  );
  const [stockMovements, setStockMovements] = usePersistentMockState<MockStockMovement[]>(
    "stockMovements",
    mockStockMovements,
  );
  const [tab, setTab] = useState<TabValue>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockInvoice | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<MockInvoice | null>(
    null,
  );
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [printRow, setPrintRow] = useState<MockInvoice | null>(null);
  /** Whether to auto-deduct stock when issuing a direct invoice (no SO). */
  const [deductStock, setDeductStock] = useState(true);

  /* ── KPIs ── */
  const kpis = useMemo(() => {
    let outstanding = 0n;
    let overdue = 0n;
    let paidMtd = 0n;
    let totalDays = 0;
    let paidCount = 0;
    const monthStart = new Date(
      new Date(NOW).getFullYear(),
      new Date(NOW).getMonth(),
      1,
    ).getTime();
    for (const inv of rows) {
      if (inv.status === "void") continue;
      if (inv.status !== "paid") {
        outstanding += inv.balanceDueCents;
        if (inv.dueDate < NOW) overdue += inv.balanceDueCents;
      }
      if (inv.status === "paid" && inv.issuedAt && inv.issuedAt >= monthStart) {
        paidMtd += inv.paidAmountInBaseCents;
        const days = Math.max(
          0,
          Math.floor((inv.updatedAt - inv.issuedAt) / 86400000),
        );
        totalDays += days;
        paidCount += 1;
      }
    }
    return {
      outstanding,
      overdue,
      paidMtd,
      avgDays: paidCount > 0 ? Math.round(totalDays / paidCount) : 0,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "overdue") {
      return rows.filter(
        (r) =>
          (r.status === "issued" || r.status === "partially_paid") &&
          r.dueDate < NOW,
      );
    }
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  const dealersById = useMemo(
    () => new Map(dealers.map((d) => [d._id, d])),
    [dealers],
  );

  const columns = useMemo<ColumnDef<MockInvoice>[]>(
    () => [
      {
        accessorKey: "invoiceNumber",
        header: "INV #",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.invoiceNumber || (
              <span className="text-(--text-tertiary)">— draft —</span>
            )}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "dealer",
        header: "Dealer",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-primary)">
            {dealersById.get(row.original.dealerId)?.displayName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "issueDate",
        header: "Issued",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.issueDate)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "dueDate",
        header: "Due",
        cell: ({ row }: { row: { original: any } }) => {
          const overdue =
            row.original.dueDate < NOW &&
            row.original.balanceDueCents > 0n &&
            row.original.status !== "void" &&
            row.original.status !== "draft";
          return (
            <span
              className={`tabular-nums ${
                overdue ? "text-(--danger)" : "text-(--text-secondary)"
              }`}
            >
              {formatDate(row.original.dueDate)}
            </span>
          );
        },
      },
      {
        accessorKey: "currency",
        header: "Ccy",
        cell: ({ row }: { row: { original: any } }) => (
          <Badge variant="neutral">{row.original.currency}</Badge>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "totalBdt",
        header: "Total (BDT)",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.totalAmountInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "paid",
        header: "Paid",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(row.original.paidAmountInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true, hideOnMobile: true },
      },
      {
        id: "balance",
        header: "Balance",
        cell: ({ row }: { row: { original: any } }) => (
          <span
            className={`font-mono tabular-nums ${
              row.original.balanceDueCents > 0n &&
              row.original.dueDate < NOW &&
              row.original.status !== "void" &&
              row.original.status !== "draft"
                ? "text-(--danger)"
                : ""
            }`}
          >
            {formatMoney(row.original.balanceDueCents, row.original.currency, {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }: { row: { original: any } }) => (
          <InvoiceStatusBadge status={effectiveStatus(row.original)} />
        ),
      },
      {
        id: "daysOverdue",
        header: "Days overdue",
        cell: ({ row }: { row: { original: any } }) => {
          const d = daysOverdue(row.original);
          if (d === 0) return <span className="text-(--text-tertiary)">—</span>;
          return (
            <span className="font-mono tabular-nums text-(--danger)">{d}</span>
          );
        },
        meta: { numeric: true, hideOnMobile: true },
      },
    ],
    [dealersById],
  );

  /* ───── Mutations (mock-only writes for now) ───── */

  /**
   * Build a MockInvoice + line rows from form values. Used by both "save
   * draft" and "save & issue" paths so the math stays in one place.
   *
   * New computation flow (unified model):
   *   1. line.subtotal      = qty × unitPrice
   *   2. line.discount      = percent or flat (per discountType)
   *   3. line.total         = subtotal − discount
   *   4. invoice.subtotal   = Σ line.total
   *   5. invoice.discount   = percent or flat (per generalDiscountType)
   *   6. afterDiscount      = invoice.subtotal − invoice.discount
   *   7. vatAmount          = afterDiscount × vatRate  (if vatEnabled)
   *   8. grandTotal         = afterDiscount + vatAmount
   *
   * For back-compat we leave the deprecated tax/discount fields (`taxRateId`,
   * `taxRatePercentE4`, `lineTaxCents`, `generalDiscountBp`, `generalDiscountCents`)
   * as null/0 on new invoices — old invoices with those fields populated still
   * render correctly via the read paths in the detail Sheet and print view.
   */
  function buildInvoiceFromForm(
    values: InvoiceFormValues,
    overrideId?: string,
  ): {
    invoice: MockInvoice;
    newLines: MockInvoiceLine[];
  } {
    const vatRate = companyProfile.defaultVatRatePercentE4;
    const totals = computeInvoiceTotals(values, vatRate);
    const invoiceId = overrideId ?? `inv_local_${Date.now()}`;
    const fxRate = 100_000_000n; // identity for non-BDT just in mock land

    const newLines: MockInvoiceLine[] = values.lines.map((l, i) => {
      const qty = BigInt(Math.max(0, Math.round(l.quantity)));
      const subtotal = l.unitPriceCents * qty;
      let discount: bigint;
      if (l.discountType === "flat") {
        discount = l.discountValue;
      } else {
        discount = (subtotal * l.discountValue) / 1_000_000n;
      }
      if (discount > subtotal) discount = subtotal;
      if (discount < 0n) discount = 0n;
      const lineTotal = subtotal - discount;
      return {
        _id: `il_local_${invoiceId}_${i}`,
        invoiceId,
        productId: l.productId,
        productVariantId: l.productVariantId,
        description: l.description || null,
        quantity: l.quantity,
        unitId: l.unitId,
        unitPriceCents: l.unitPriceCents,
        // New unified discount model
        discountType: l.discountType,
        discountValue: l.discountValue,
        netUnitPriceCents:
          qty > 0n ? lineTotal / qty : l.unitPriceCents,
        // Back-compat — null out per-line tax. Old invoices that have these
        // populated still render correctly via the read paths.
        taxRateId: null,
        taxRatePercentE4: 0n,
        lineSubtotalCents: subtotal,
        lineDiscountCents: discount,
        lineTaxCents: 0n,
        lineTotalCents: lineTotal,
        fxRateToBaseE8: fxRate,
        lineTotalInBaseCents: lineTotal,
        sortOrder: i,
      };
    });

    const invoice: MockInvoice = {
      _id: invoiceId,
      invoiceNumber: "",
      salesOrderId: null,
      dealerId: values.dealerId,
      branchId: values.branchId,
      status: "draft",
      currency: values.currency.toUpperCase(),
      fxRateToBaseE8: fxRate,
      subtotalAmountCents: totals.subtotal,
      discountAmountCents: totals.lineDiscounts + totals.invoiceDiscount,
      taxAmountCents: totals.vatAmount,
      shippingAmountCents: 0n,
      totalAmountCents: totals.grandTotal,
      totalAmountInBaseCents: totals.grandTotal,
      paidAmountCents: 0n,
      paidAmountInBaseCents: 0n,
      balanceDueCents: totals.grandTotal,
      issueDate: new Date(values.issueDate).getTime(),
      dueDate: new Date(values.dueDate).getTime(),
      issuedAt: null,
      voidedAt: null,
      voidReason: null,
      notes: values.notes || null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      // New unified discount + VAT fields
      generalDiscountType: values.generalDiscountType,
      generalDiscountValue: values.generalDiscountValue,
      vatEnabled: values.vatEnabled,
      vatAmountCents: totals.vatAmount,
      // Back-compat — explicitly null out the old discount fields on new
      // invoices.
      generalDiscountBp: null,
      generalDiscountCents: null,
      generalDiscountReason: null,
    };
    return { invoice, newLines };
  }

  function nextInvoiceNumber(): string {
    // Prefer the new document prefix system; fall back to the legacy
    // invoice numbering config if no document prefixes are set.
    const docPrefix = companyProfile.documentPrefixes?.invoice;
    if (docPrefix) {
      const existingNumbers = rows
        .map((r) => r.invoiceNumber)
        .filter((n) => n.length > 0);
      return generateNumber("invoice", existingNumbers, docPrefix);
    }
    return getNextInvoiceNumber(
      invoiceConfig,
      rows.map((r) => r.invoiceNumber),
    );
  }

  /**
   * Deduct stock for a direct invoice (one without a linked sales order).
   * Only called when `deductStock` is true AND the invoice has no SO link.
   * Uses the same `sales_shipment` movement type so the inventory page
   * aggregation treats it identically to SO-based shipments.
   */
  function deductStockForInvoice(
    invoiceId: string,
    invoiceLines: ReadonlyArray<{
      productId: string;
      productVariantId: string | null;
      quantity: number;
      unitPriceCents: bigint;
    }>,
    salesOrderId: string | null,
  ) {
    // Skip if linked to a sales order — stock was already deducted on SO shipment.
    if (salesOrderId) return;
    if (!deductStock) return;
    if (invoiceLines.length === 0) return;

    // Resolve warehouse: prefer a finished_goods warehouse, fall back
    // to the first active warehouse.
    const fgWarehouse =
      warehouses.find((w) => w.warehouseType === "finished_goods" && w.isActive) ??
      warehouses.find((w) => w.isActive);
    const whId = fgWarehouse?._id ?? "wh_default";

    const movements = createSalesShipmentMovements(
      invoiceId,
      invoiceLines.map((l) => ({
        productId: l.productId,
        variantId: l.productVariantId,
        quantityOrdered: l.quantity,
        unitPriceCents: l.unitPriceCents,
      })),
      whId,
    );
    setStockMovements((prev) => [...prev, ...movements]);
    toast.success("Stock updated", {
      description: `${movements.length} sales shipment movement(s) deducted from inventory.`,
    });
  }

  function handleSubmitNew(values: InvoiceFormValues) {
    if (!isMock) return;
    const { invoice, newLines } = buildInvoiceFromForm(values);
    if (values.intent === "issue") {
      invoice.invoiceNumber = nextInvoiceNumber();
      invoice.status = "issued";
      invoice.issuedAt = Date.now();
    }
    setRows((prev) => [invoice, ...prev]);
    setLines((prev) => [...prev, ...newLines]);
    setCreateOpen(false);
    if (values.intent === "issue") {
      // Auto stock-out for direct invoices (no linked sales order).
      deductStockForInvoice(
        invoice._id,
        newLines.map((l) => ({
          productId: l.productId,
          productVariantId: l.productVariantId,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
        })),
        invoice.salesOrderId,
      );
      toast.success(`Issued ${invoice.invoiceNumber}`);
    } else {
      toast.success("Draft invoice saved (mock)");
    }
  }

  function handleEditDraft(values: InvoiceFormValues) {
    if (!isMock || !editingInvoice) return;
    if (editingInvoice.status !== "draft") {
      toast.error("Only draft invoices can be edited");
      return;
    }
    const { invoice, newLines } = buildInvoiceFromForm(
      values,
      editingInvoice._id,
    );
    // If the user clicked Save & Issue from the edit form, promote to issued.
    if (values.intent === "issue") {
      invoice.invoiceNumber = nextInvoiceNumber();
      invoice.status = "issued";
      invoice.issuedAt = Date.now();
    } else {
      // Preserve the existing invoice number / created timestamp on simple
      // draft re-saves.
      invoice.invoiceNumber = editingInvoice.invoiceNumber;
      invoice.createdAt = editingInvoice.createdAt;
    }
    setRows((prev) =>
      prev.map((r) => (r._id === editingInvoice._id ? invoice : r)),
    );
    setLines((prev) => [
      ...prev.filter((l) => l.invoiceId !== editingInvoice._id),
      ...newLines,
    ]);
    setDetailRow((prev) =>
      prev && prev._id === editingInvoice._id ? invoice : prev,
    );
    setEditingInvoice(null);
    if (values.intent === "issue") {
      // Auto stock-out for direct invoices (no linked sales order).
      deductStockForInvoice(
        invoice._id,
        newLines.map((l) => ({
          productId: l.productId,
          productVariantId: l.productVariantId,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
        })),
        invoice.salesOrderId,
      );
      toast.success(`Issued ${invoice.invoiceNumber}`);
    } else {
      toast.success("Draft invoice updated (mock)");
    }
  }

  function handleIssue(invoice: MockInvoice) {
    if (!isMock) return;
    const number = nextInvoiceNumber();
    const stamp = Date.now();
    setRows((prev) =>
      prev.map((r) =>
        r._id === invoice._id
          ? {
              ...r,
              invoiceNumber: number,
              status: "issued",
              issuedAt: stamp,
              updatedAt: stamp,
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === invoice._id
        ? {
            ...prev,
            invoiceNumber: number,
            status: "issued",
            issuedAt: stamp,
            updatedAt: stamp,
          }
        : prev,
    );
    // Auto stock-out for direct invoices (no linked sales order).
    const invoiceLines = lines.filter((l) => l.invoiceId === invoice._id);
    deductStockForInvoice(
      invoice._id,
      invoiceLines.map((l) => ({
        productId: l.productId,
        productVariantId: l.productVariantId,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
      })),
      invoice.salesOrderId,
    );
    toast.success(`Issued ${number}`);
  }

  function handleVoid(invoice: MockInvoice) {
    if (!isMock) return;
    setRows((prev) =>
      prev.map((r) =>
        r._id === invoice._id
          ? {
              ...r,
              status: "void",
              voidedAt: Date.now(),
              voidReason: "User-voided in admin",
              updatedAt: Date.now(),
            }
          : r,
      ),
    );
    setDetailRow(null);
    toast.success("Invoice voided (mock)");
  }

  function handleApplyCreditNote(invoice: MockInvoice) {
    if (!isMock) return;
    const cn = creditNotes.find(
      (c) =>
        c.dealerId === invoice.dealerId &&
        c.status === "issued" &&
        c.remainingAmountCents > 0n,
    );
    if (!cn) {
      toast.error("No applicable credit note for this dealer");
      return;
    }
    const applyAmt =
      cn.remainingAmountCents < invoice.balanceDueCents
        ? cn.remainingAmountCents
        : invoice.balanceDueCents;
    const newPaid = invoice.paidAmountCents + applyAmt;
    const newBalance = invoice.totalAmountCents - newPaid;
    const status: InvoiceStatus = newBalance <= 0n ? "paid" : "partially_paid";
    setRows((prev) =>
      prev.map((r) =>
        r._id === invoice._id
          ? {
              ...r,
              paidAmountCents: newPaid,
              paidAmountInBaseCents: newPaid,
              balanceDueCents: newBalance < 0n ? 0n : newBalance,
              status,
              updatedAt: Date.now(),
            }
          : r,
      ),
    );
    setPayments((prev) => [
      {
        _id: `pay_local_${Date.now()}`,
        invoiceId: invoice._id,
        paymentNumber: `PAY-LOCAL-${Date.now().toString().slice(-6)}`,
        paymentMethod: "credit_note",
        currency: invoice.currency,
        amountCents: applyAmt,
        fxRateToBaseE8: invoice.fxRateToBaseE8,
        amountInBaseCents: applyAmt,
        externalReference: cn.creditNoteNumber,
        receivedAt: Date.now(),
        bankAccountName: null,
        creditNoteId: cn._id,
        notes: `Applied ${cn.creditNoteNumber}`,
        voidedAt: null,
      },
      ...prev,
    ]);
    setDetailRow(null);
    toast.success(
      `Applied ${cn.creditNoteNumber} (${formatMoney(applyAmt, invoice.currency, {
        locale: "en-BD",
      })})`,
    );
  }

  function handleRecordPayment(values: PaymentFormValues) {
    if (!detailRow || !isMock) return;
    const inv = detailRow;
    const newPaid = inv.paidAmountCents + values.amountCents;
    const newBalance = inv.totalAmountCents - newPaid;
    const status: InvoiceStatus = newBalance <= 0n ? "paid" : "partially_paid";
    const paymentNumber = `PAY-LOCAL-${Date.now().toString().slice(-6)}`;
    setRows((prev) =>
      prev.map((r) =>
        r._id === inv._id
          ? {
              ...r,
              paidAmountCents: newPaid,
              paidAmountInBaseCents: newPaid,
              balanceDueCents: newBalance < 0n ? 0n : newBalance,
              status,
              updatedAt: Date.now(),
            }
          : r,
      ),
    );
    setPayments((prev) => [
      {
        _id: `pay_local_${Date.now()}`,
        invoiceId: inv._id,
        paymentNumber,
        paymentMethod: values.paymentMethod,
        currency: inv.currency,
        amountCents: values.amountCents,
        fxRateToBaseE8: inv.fxRateToBaseE8,
        amountInBaseCents: values.amountCents,
        externalReference: values.externalReference || null,
        receivedAt: new Date(values.receivedAt).getTime(),
        bankAccountName: values.bankAccountName || null,
        creditNoteId: null,
        notes: values.notes || null,
        voidedAt: null,
      },
      ...prev,
    ]);
    setDetailRow(null);
    setPaymentOpen(false);
    toast.success(
      `Recorded ${formatMoney(values.amountCents, inv.currency, {
        locale: "en-BD",
      })} via ${values.paymentMethod.replace("_", " ")}`,
    );
  }

  /** Inline payment from within the invoice detail Sheet. Updates both
   *  payments and the invoice row but keeps the detail Sheet open so the
   *  user can see the updated balance immediately. */
  function handleInlinePayment(values: PaymentFormValues) {
    if (!detailRow || !isMock) return;
    const inv = detailRow;
    const newPaid = inv.paidAmountCents + values.amountCents;
    const newBalance = inv.totalAmountCents - newPaid;
    const status: InvoiceStatus = newBalance <= 0n ? "paid" : "partially_paid";
    const paymentNumber = `PAY-LOCAL-${Date.now().toString().slice(-6)}`;
    const updatedInvoice = {
      ...inv,
      paidAmountCents: newPaid,
      paidAmountInBaseCents: newPaid,
      balanceDueCents: newBalance < 0n ? 0n : newBalance,
      status,
      updatedAt: Date.now(),
    };
    setRows((prev) =>
      prev.map((r) => (r._id === inv._id ? updatedInvoice : r)),
    );
    setPayments((prev) => [
      {
        _id: `pay_local_${Date.now()}`,
        invoiceId: inv._id,
        paymentNumber,
        paymentMethod: values.paymentMethod,
        currency: inv.currency,
        amountCents: values.amountCents,
        fxRateToBaseE8: inv.fxRateToBaseE8,
        amountInBaseCents: values.amountCents,
        externalReference: values.externalReference || null,
        receivedAt: new Date(values.receivedAt).getTime(),
        bankAccountName: values.bankAccountName || null,
        creditNoteId: null,
        notes: values.notes || null,
        voidedAt: null,
      },
      ...prev,
    ]);
    // Keep detail sheet open so user sees updated balance
    setDetailRow(updatedInvoice);
    toast.success(
      `Recorded ${formatMoney(values.amountCents, inv.currency, {
        locale: "en-BD",
      })} via ${values.paymentMethod.replace("_", " ")}`,
    );
  }

  function handleReversePayment(invoice: MockInvoice, payment: MockPayment) {
    if (!isMock) return;
    setPayments((prev) =>
      prev.map((p) =>
        p._id === payment._id ? { ...p, voidedAt: Date.now() } : p,
      ),
    );
    const newPaid = invoice.paidAmountCents - payment.amountCents;
    const newBalance = invoice.totalAmountCents - newPaid;
    const status: InvoiceStatus =
      newPaid <= 0n
        ? "issued"
        : newBalance <= 0n
          ? "paid"
          : "partially_paid";
    setRows((prev) =>
      prev.map((r) =>
        r._id === invoice._id
          ? {
              ...r,
              paidAmountCents: newPaid < 0n ? 0n : newPaid,
              paidAmountInBaseCents: newPaid < 0n ? 0n : newPaid,
              balanceDueCents: newBalance < 0n ? 0n : newBalance,
              status,
              updatedAt: Date.now(),
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === invoice._id
        ? {
            ...prev,
            paidAmountCents: newPaid < 0n ? 0n : newPaid,
            paidAmountInBaseCents: newPaid < 0n ? 0n : newPaid,
            balanceDueCents: newBalance < 0n ? 0n : newBalance,
            status,
            updatedAt: Date.now(),
          }
        : prev,
    );
    toast.success(`Reversed ${payment.paymentNumber}`);
  }

  /* ── Print routing ── */
  const printPortalRef = useRef<HTMLDivElement>(null);

  function openPrint(invoice: MockInvoice) {
    setPrintRow(invoice);
  }

  // Once the PrintPortal mounts with the invoice data, clone its content
  // into a hidden iframe via `printElement` and trigger the browser print
  // dialog. This replaces the old `window.print()` approach which relied on
  // fragile `@media print { body * { visibility: hidden } }` hacks.
  useEffect(() => {
    if (!printRow) return;
    const id = window.setTimeout(() => {
      const el = printPortalRef.current;
      if (el) {
        printElement(el, {
          title: `Invoice ${printRow.invoiceNumber || "DRAFT"}`,
        });
      }
      setPrintRow(null);
    }, 120);
    return () => window.clearTimeout(id);
  }, [printRow]);

  /* ── Dealer detail map for credit status in the invoice form ── */
  const dealerDetailsMap = useMemo(() => {
    const map = new Map<string, DealerDetailInfo>();
    for (const d of dealers) {
      const tier = findDealerTier(d.tierId);
      const outstanding = dealerOutstandingBaseCents(d._id, rows);
      map.set(d._id, {
        id: d._id,
        displayName: d.displayName,
        legalName: d.legalName,
        tierLabel: tier?.displayName ?? null,
        contactPhone: d.contactJson?.phone ?? null,
        contactEmail: d.contactJson?.email ?? null,
        contactPerson: d.contactJson?.contactPerson ?? null,
        creditLimitCents: d.creditLimitCents,
        creditLimitCurrency: d.creditLimitCurrency,
        outstandingCents: outstanding,
      });
    }
    return map;
  }, [dealers, rows]);

  /* ── Reusable form props (avoids re-computing per render). ── */
  const formProps = useMemo(
    () => ({
      dealers: dealers.map((d) => ({
        id: d._id,
        displayName: d.displayName,
      })),
      branches: branches.map((b) => ({
        id: b._id,
        displayName: b.displayName,
      })),
      products: products.map((p) => ({
        id: p._id,
        displayName: p.displayName,
        defaultPriceCents: BigInt(
          Math.round(Number(p.standardCostCents) * 1.6),
        ),
        unitId: p.primaryUnitId,
      })),
      vatRatePercentE4: companyProfile.defaultVatRatePercentE4,
      resolvePrice: (dealerId: string, productId: string) => {
        const r = resolvePriceMock(dealerId, productId);
        return {
          unitPriceCents: r.unitPriceCents,
          sourceLabel: r.sourceLabel,
        };
      },
      dealerDetails: dealerDetailsMap,
    }),
    [dealers, branches, products, companyProfile.defaultVatRatePercentE4, dealerDetailsMap],
  );

  function handleExportInvoices(extension: "csv" | "xls") {
    const columns: ExportColumn<MockInvoice>[] = [
      {
        key: "invoiceNumber",
        label: "Inv #",
        format: (v) => (typeof v === "string" && v.length > 0 ? v : "(draft)"),
      },
      {
        key: "dealerId",
        label: "Dealer",
        value: (r) => dealersById.get(r.dealerId)?.displayName ?? r.dealerId,
      },
      { key: "issueDate", label: "Date", format: (v) => formatDateForCsv(Number(v)) },
      { key: "dueDate", label: "Due", format: (v) => formatDateForCsv(Number(v)) },
      // Money in BDT base units so spreadsheets can sum across currencies.
      { key: "totalAmountInBaseCents", label: "Total BDT" },
      { key: "paidAmountInBaseCents", label: "Paid BDT" },
      // Balance in invoice-native currency; pair with the Currency column.
      { key: "balanceDueCents", label: "Balance" },
      { key: "currency", label: "Currency" },
      {
        key: "status",
        label: "Status",
        value: (r) => effectiveStatus(r),
      },
    ];
    downloadCsv(
      exportFilename("invoices", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Invoices"
        description="Issued invoices, partial payments, and overdue receivables."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportInvoices("csv")}
              onExcel={() => handleExportInvoices("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New invoice
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Invoice summary"
      >
        <KpiCard
          caption="Outstanding"
          value={formatMoneySmart(kpis.outstanding, "BDT", { locale: "en-BD" })}
          hint="Open invoice balance"
        />
        <KpiCard
          caption="Overdue"
          value={
            <span className="text-(--danger)">
              {formatMoneySmart(kpis.overdue, "BDT", { locale: "en-BD" })}
            </span>
          }
          hint="Past due date"
        />
        <KpiCard
          caption="Paid MTD"
          value={formatMoneySmart(kpis.paidMtd, "BDT", { locale: "en-BD" })}
          hint="Month-to-date receipts"
        />
        <KpiCard
          caption="Avg days to pay"
          value={kpis.avgDays}
          hint="DSO over paid invoices"
        />
      </section>

      <Tabs value={tab} onValueChange={(v: string) => setTab(v as TabValue)}>
        <TabsList>
          {TAB_OPTIONS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No invoices match"
        emptyDescription={
          rows.length === 0
            ? "Generate invoices from delivered orders to start tracking AR."
            : "Try a different tab."
        }
        emptyIcon={<FileText className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New invoice
            </Button>
          ) : undefined
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg" className="sm:max-w-5xl">
          <SheetHeader>
            <SheetTitle>New invoice</SheetTitle>
            <SheetDescription>
              Save as draft to revisit later, or issue immediately to assign a
              sequence number.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <div className="mb-4 flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-medium text-(--text-primary)">
                  Deduct from stock
                </span>
                <span className="text-[11px] text-(--text-tertiary)">
                  When issued, deduct line quantities from finished-goods inventory.
                </span>
              </div>
              <Switch
                checked={deductStock}
                onCheckedChange={setDeductStock}
                aria-label="Deduct from stock on issue"
              />
            </div>
            <InvoiceForm
              {...formProps}
              onSubmit={handleSubmitNew}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail / edit */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDetailRow(null);
            setEditingInvoice(null);
          }
        }}
      >
        <SheetContent
          side="right"
          size="lg"
          className={editingInvoice ? "sm:max-w-5xl" : undefined}
        >
          {detailRow && editingInvoice ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit draft invoice</SheetTitle>
                <SheetDescription>
                  Drafts can be reshaped freely. Once issued, line edits are
                  locked — issue a credit note instead.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="mb-4 flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-medium text-(--text-primary)">
                      Deduct from stock
                    </span>
                    <span className="text-[11px] text-(--text-tertiary)">
                      When issued, deduct line quantities from finished-goods inventory.
                    </span>
                  </div>
                  <Switch
                    checked={deductStock}
                    onCheckedChange={setDeductStock}
                    aria-label="Deduct from stock on issue"
                  />
                </div>
                <InvoiceForm
                  {...formProps}
                  initialValues={{
                    dealerId: editingInvoice.dealerId,
                    branchId: editingInvoice.branchId,
                    currency: editingInvoice.currency,
                    issueDate: new Date(editingInvoice.issueDate)
                      .toISOString()
                      .slice(0, 10),
                    dueDate: new Date(editingInvoice.dueDate)
                      .toISOString()
                      .slice(0, 10),
                    notes: editingInvoice.notes ?? "",
                    lines: lines
                      .filter((l) => l.invoiceId === editingInvoice._id)
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((l) => {
                        // Prefer the new fields; fall back to legacy bp if a
                        // pre-migration row is being edited.
                        const discountType: "percent" | "flat" =
                          l.discountType ?? "percent";
                        let discountValue: bigint;
                        if (l.discountValue !== undefined) {
                          discountValue = l.discountValue;
                        } else if (l.discountPercentE4 !== undefined) {
                          discountValue = l.discountPercentE4;
                        } else if (l.lineDiscountBp !== undefined) {
                          discountValue = BigInt(l.lineDiscountBp * 100);
                        } else {
                          discountValue = 0n;
                        }
                        return {
                          productId: l.productId,
                          productVariantId: l.productVariantId,
                          description: l.description ?? "",
                          quantity: l.quantity,
                          unitId: l.unitId,
                          unitPriceCents: l.unitPriceCents,
                          discountType,
                          discountValue,
                        };
                      }),
                    generalDiscountType:
                      editingInvoice.generalDiscountType ??
                      // Back-compat: derive from legacy fields if present.
                      (editingInvoice.generalDiscountCents !== null &&
                      editingInvoice.generalDiscountCents !== undefined &&
                      editingInvoice.generalDiscountCents > 0n
                        ? "flat"
                        : "percent"),
                    generalDiscountValue:
                      editingInvoice.generalDiscountValue ??
                      // Back-compat fallback: prefer cents, else bp×100.
                      (editingInvoice.generalDiscountCents !== null &&
                      editingInvoice.generalDiscountCents !== undefined &&
                      editingInvoice.generalDiscountCents > 0n
                        ? editingInvoice.generalDiscountCents
                        : editingInvoice.generalDiscountBp !== null &&
                            editingInvoice.generalDiscountBp !== undefined
                          ? BigInt(editingInvoice.generalDiscountBp * 100)
                          : 0n),
                    vatEnabled: editingInvoice.vatEnabled ?? false,
                  }}
                  onSubmit={handleEditDraft}
                  onCancel={() => setEditingInvoice(null)}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <InvoiceDetail
              row={detailRow}
              lines={lines
                .filter((l) => l.invoiceId === detailRow._id)
                .sort((a, b) => a.sortOrder - b.sortOrder)}
              payments={payments.filter(
                (p) => p.invoiceId === detailRow._id,
              )}
              dealersById={dealersById}
              products={products}
              creditNotes={creditNotes}
              onIssue={() => handleIssue(detailRow)}
              onVoid={() => handleVoid(detailRow)}
              onEdit={() => setEditingInvoice(detailRow)}
              onRecordPayment={() => setPaymentOpen(true)}
              onInlinePayment={handleInlinePayment}
              onApplyCreditNote={() => handleApplyCreditNote(detailRow)}
              onReversePayment={(p) => handleReversePayment(detailRow, p)}
              onPrint={() => openPrint(detailRow)}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Payment */}
      <Sheet open={paymentOpen} onOpenChange={setPaymentOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Record payment</SheetTitle>
            <SheetDescription>
              {detailRow
                ? `Against ${detailRow.invoiceNumber || "(draft)"} — balance ${formatMoney(
                    detailRow.balanceDueCents,
                    detailRow.currency,
                    { locale: "en-BD" },
                  )}.`
                : null}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            {detailRow ? (
              <PaymentForm
                defaultAmountCents={detailRow.balanceDueCents}
                currency={detailRow.currency}
                maxAmountCents={detailRow.balanceDueCents}
                onSubmit={handleRecordPayment}
                onCancel={() => setPaymentOpen(false)}
              />
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Print preview — mounts a hidden InvoicePrintView; the useEffect above
          clones it into a print iframe via printElement, then unmounts. */}
      {printRow ? (
        <PrintPortal
          ref={printPortalRef}
          row={printRow}
          lines={lines
            .filter((l) => l.invoiceId === printRow._id)
            .sort((a, b) => a.sortOrder - b.sortOrder)}
          payments={payments.filter((p) => p.invoiceId === printRow._id)}
          dealer={dealersById.get(printRow.dealerId) ?? null}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

type InvoiceDetailProps = {
  row: MockInvoice;
  lines: MockInvoiceLine[];
  payments: MockPayment[];
  dealersById: Map<string, MockDealerRecord>;
  products: MockProduct[];
  creditNotes: MockCreditNote[];
  onIssue: () => void;
  onVoid: () => void;
  onEdit: () => void;
  onRecordPayment: () => void;
  onInlinePayment: (values: PaymentFormValues) => void;
  onApplyCreditNote: () => void;
  onReversePayment: (p: MockPayment) => void;
  onPrint: () => void;
};

function InvoiceDetail({
  row,
  lines,
  payments,
  dealersById,
  products,
  creditNotes,
  onIssue,
  onVoid,
  onEdit,
  onRecordPayment,
  onInlinePayment,
  onApplyCreditNote,
  onReversePayment,
  onPrint,
}: InvoiceDetailProps) {
  const [inlinePayOpen, setInlinePayOpen] = useState(false);
  const dealer = dealersById.get(row.dealerId) ?? null;
  const productsById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const dealerCreditNotes = useMemo(
    () =>
      creditNotes.filter(
        (c) =>
          c.dealerId === row.dealerId &&
          c.status === "issued" &&
          c.remainingAmountCents > 0n,
      ),
    [creditNotes, row.dealerId],
  );

  const isOverdue =
    row.balanceDueCents > 0n &&
    row.dueDate < NOW &&
    row.status !== "void" &&
    row.status !== "draft";
  const overdueDays = daysOverdue(row);
  const canEdit = row.status === "draft";

  // Compute the displayed invoice-level discount. Prefer the new fields
  // (`generalDiscountType`/`generalDiscountValue`); fall back to legacy
  // `generalDiscountCents` / `generalDiscountBp` for old invoices.
  let generalDiscount = 0n;
  if (
    row.generalDiscountType !== undefined &&
    row.generalDiscountValue !== undefined
  ) {
    if (row.generalDiscountType === "flat") {
      generalDiscount = row.generalDiscountValue;
    } else if (row.generalDiscountType === "percent") {
      // Applied against the lines subtotal stored on the row — matches what
      // the form's preview math computes.
      generalDiscount =
        (row.subtotalAmountCents * row.generalDiscountValue) / 1_000_000n;
    }
  } else if (
    row.generalDiscountCents !== null &&
    row.generalDiscountCents !== undefined
  ) {
    generalDiscount = row.generalDiscountCents;
  } else if (
    row.generalDiscountBp !== null &&
    row.generalDiscountBp !== undefined &&
    row.generalDiscountBp > 0
  ) {
    const preGeneral =
      row.subtotalAmountCents -
      row.discountAmountCents +
      row.taxAmountCents +
      row.shippingAmountCents;
    generalDiscount = (preGeneral * BigInt(row.generalDiscountBp)) / 10_000n;
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.invoiceNumber || "DRAFT"}</span>
          <InvoiceStatusBadge status={effectiveStatus(row)} />
          {isOverdue ? (
            <Badge variant="danger">{overdueDays}d overdue</Badge>
          ) : null}
          <RecordAuditBadge tableName="invoices" recordId={row._id} />
          <AttachmentBadge
            parentTable="invoices"
            parentRecordId={row._id}
          />
        </SheetTitle>
        <SheetDescription>
          {dealer?.displayName ?? "—"} · Issued {formatDate(row.issueDate)} ·
          Due{" "}
          <span className={isOverdue ? "text-(--danger)" : undefined}>
            {formatDate(row.dueDate)}
          </span>
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              {/* Action row — rendered prominently at top of body */}
              <section className="flex flex-wrap gap-2 -mb-1">
                {row.status === "draft" ? (
                  <ConfirmAction
                    trigger={<Button>Issue invoice</Button>}
                    title="Issue invoice now?"
                    description={
                      <>
                        A sequence number will be assigned (
                        <span className="font-mono">INV-2026-XXXXXX</span>) and
                        the invoice becomes immutable.
                      </>
                    }
                    confirmLabel="Confirm & issue"
                    onConfirm={onIssue}
                  />
                ) : null}
                {(row.status === "issued" ||
                  row.status === "partially_paid") && (
                  <Button onClick={onRecordPayment}>Record payment</Button>
                )}
                {dealerCreditNotes.length > 0 &&
                (row.status === "issued" ||
                  row.status === "partially_paid") ? (
                  <ConfirmAction
                    trigger={
                      <Button variant="outline">Apply credit note</Button>
                    }
                    title={`Apply ${dealerCreditNotes[0]!.creditNoteNumber}?`}
                    description={
                      <>
                        Up to{" "}
                        <span className="font-mono">
                          {formatMoney(
                            dealerCreditNotes[0]!.remainingAmountCents,
                            dealerCreditNotes[0]!.currency,
                            { locale: "en-BD" },
                          )}
                        </span>{" "}
                        will be applied against this invoice.
                      </>
                    }
                    confirmLabel="Apply"
                    onConfirm={onApplyCreditNote}
                  />
                ) : null}
                <Button variant="outline" onClick={onPrint}>
                  <Printer className="h-3.5 w-3.5" /> Print
                </Button>
                {row.status !== "paid" &&
                row.status !== "void" &&
                row.status !== "draft" ? (
                  <Button
                    variant="outline"
                    onClick={() => toast.info("Reminder sent (mock)")}
                  >
                    Send reminder
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
                    <Pencil className="size-4" />
                  </Button>
                ) : (
                  <Tooltip content="Cannot edit closed records — create a credit note instead">
                    <span>
                      <Button variant="ghost" size="icon" disabled aria-label="Edit">
                        <Pencil className="size-4" />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {row.status !== "paid" && row.status !== "void" ? (
                  <ConfirmAction
                    trigger={<Button variant="destructive">Void</Button>}
                    title="Void this invoice?"
                    description="Voided invoices cannot be reissued. Payments will need to be reversed manually first."
                    confirmLabel="Void"
                    confirmVariant="destructive"
                    onConfirm={onVoid}
                  />
                ) : null}
              </section>

              {/* Totals snapshot grid */}
              <section className="grid grid-cols-2 gap-3">
                <DetailField
                  label="Total"
                  value={formatMoney(row.totalAmountCents, row.currency, {
                    locale: "en-BD",
                    display: "code",
                  })}
                />
                <DetailField
                  label="Total (BDT)"
                  value={formatMoney(row.totalAmountInBaseCents, "BDT", {
                    locale: "en-BD",
                  })}
                />
                <DetailField
                  label="Paid"
                  value={formatMoney(row.paidAmountCents, row.currency, {
                    locale: "en-BD",
                    display: "code",
                  })}
                />
                <DetailField
                  label="Balance due"
                  value={
                    <span className={isOverdue ? "text-(--danger)" : undefined}>
                      {formatMoney(row.balanceDueCents, row.currency, {
                        locale: "en-BD",
                        display: "code",
                      })}
                    </span>
                  }
                />
              </section>

              {/* Lines */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Line items ({lines.length})
                </div>
                {lines.length === 0 ? (
                  <div className="text-[12px] text-(--text-tertiary)">
                    No line data.
                  </div>
                ) : (
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle)">
                        <th className="py-2">Item</th>
                        <th className="text-right">Qty</th>
                        <th className="text-right">Unit</th>
                        <th className="text-right">Disc</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const product = productsById.get(l.productId);
                        // Format a human description of the line discount.
                        // Prefer the new fields, fall back to legacy bp.
                        let discountHint: string | null = null;
                        if (
                          l.discountType === "percent" &&
                          l.discountValue > 0n
                        ) {
                          discountHint = `${(
                            Number(l.discountValue) / 10_000
                          ).toFixed(2)}% line discount`;
                        } else if (
                          l.discountType === "flat" &&
                          l.discountValue > 0n
                        ) {
                          discountHint = "flat line discount";
                        } else if (
                          l.lineDiscountBp !== undefined &&
                          l.lineDiscountBp > 0
                        ) {
                          discountHint = `${(l.lineDiscountBp / 100).toFixed(
                            2,
                          )}% line discount`;
                        }
                        return (
                          <tr
                            key={l._id}
                            className="border-b border-(--border-subtle)"
                          >
                            <td className="py-2 text-(--text-primary)">
                              <div>
                                {l.description ?? product?.displayName ?? "—"}
                              </div>
                              {discountHint ? (
                                <div className="text-[11px] text-(--text-tertiary)">
                                  {discountHint}
                                </div>
                              ) : null}
                            </td>
                            <td className="text-right tabular-nums">
                              {l.quantity}
                            </td>
                            <td className="text-right font-mono tabular-nums">
                              {formatMoney(l.unitPriceCents, row.currency, {
                                locale: "en-BD",
                                display: "none",
                              })}
                            </td>
                            <td className="text-right font-mono tabular-nums text-(--danger)">
                              {l.lineDiscountCents > 0n
                                ? `− ${formatMoney(l.lineDiscountCents, row.currency, {
                                    locale: "en-BD",
                                    display: "none",
                                  })}`
                                : "—"}
                            </td>
                            <td className="text-right font-mono tabular-nums">
                              {formatMoney(l.lineTotalCents, row.currency, {
                                locale: "en-BD",
                                display: "none",
                              })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>

              {/* Totals breakdown */}
              <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) p-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] tabular-nums">
                  <span className="text-(--text-secondary)">Subtotal</span>
                  <span className="text-right font-mono">
                    {formatMoney(row.subtotalAmountCents, row.currency, {
                      locale: "en-BD",
                    })}
                  </span>
                  {row.discountAmountCents > 0n ? (
                    <>
                      <span className="text-(--text-secondary)">
                        Line discounts
                      </span>
                      <span className="text-right font-mono text-(--danger)">
                        −{" "}
                        {formatMoney(row.discountAmountCents, row.currency, {
                          locale: "en-BD",
                        })}
                      </span>
                    </>
                  ) : null}
                  {row.taxAmountCents > 0n ? (
                    <>
                      <span className="text-(--text-secondary)">VAT</span>
                      <span className="text-right font-mono">
                        {formatMoney(row.taxAmountCents, row.currency, {
                          locale: "en-BD",
                        })}
                      </span>
                    </>
                  ) : null}
                  {row.shippingAmountCents > 0n ? (
                    <>
                      <span className="text-(--text-secondary)">Shipping</span>
                      <span className="text-right font-mono">
                        {formatMoney(row.shippingAmountCents, row.currency, {
                          locale: "en-BD",
                        })}
                      </span>
                    </>
                  ) : null}
                  {generalDiscount > 0n ? (
                    <>
                      <span className="text-(--text-secondary)">
                        Invoice discount
                        {row.generalDiscountReason ? (
                          <span className="block text-[11px] text-(--text-tertiary)">
                            ({row.generalDiscountReason})
                          </span>
                        ) : null}
                      </span>
                      <span className="text-right font-mono text-(--danger)">
                        −{" "}
                        {formatMoney(generalDiscount, row.currency, {
                          locale: "en-BD",
                        })}
                      </span>
                    </>
                  ) : null}
                  <div className="col-span-2 mt-1 border-t border-(--border-subtle) pt-2 flex items-center justify-between">
                    <span className="text-(--text-primary) font-medium">
                      Grand total
                    </span>
                    <span className="font-mono text-[15px] font-semibold">
                      {formatMoney(row.totalAmountCents, row.currency, {
                        locale: "en-BD",
                      })}
                    </span>
                  </div>
                </div>
              </section>

              {/* Payments section */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                  Payments (
                  {payments.filter((p) => p.voidedAt === null).length})
                </div>
                {payments.length === 0 ? (
                  <div className="text-[12px] text-(--text-tertiary)">
                    No payments yet.
                  </div>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {payments.map((p) => (
                      <li
                        key={p._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono text-[11px]">
                            {p.paymentNumber}
                          </span>
                          <PaymentMethodBadge method={p.paymentMethod} />
                          {p.externalReference ? (
                            <span className="text-[11px] text-(--text-tertiary) font-mono">
                              {p.externalReference}
                            </span>
                          ) : null}
                          <span className="text-[11px] text-(--text-tertiary)">
                            {new Date(p.receivedAt).toLocaleDateString("en-BD", {
                              month: "short",
                              day: "2-digit",
                            })}
                          </span>
                          {p.voidedAt ? (
                            <Badge variant="neutral">Reversed</Badge>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="font-mono tabular-nums text-[13px]">
                            {formatMoney(p.amountCents, p.currency, {
                              locale: "en-BD",
                              display: "code",
                            })}
                          </div>
                          {!p.voidedAt &&
                          row.status !== "void" &&
                          row.status !== "draft" ? (
                            <ConfirmAction
                              trigger={
                                <Button size="sm" variant="ghost">
                                  Reverse
                                </Button>
                              }
                              title="Reverse this payment?"
                              description={
                                <>
                                  Reverses{" "}
                                  <span className="font-mono">
                                    {p.paymentNumber}
                                  </span>{" "}
                                  ({formatMoney(p.amountCents, p.currency, {
                                    locale: "en-BD",
                                  })}). The invoice balance will increase
                                  accordingly.
                                </>
                              }
                              confirmLabel="Reverse"
                              confirmVariant="destructive"
                              onConfirm={() => onReversePayment(p)}
                            />
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Balance remaining */}
                <div className="mt-3 flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2">
                  <span className="text-[12px] text-(--text-tertiary) uppercase tracking-wide">
                    Balance remaining
                  </span>
                  <span
                    className={`font-mono tabular-nums text-[14px] font-semibold ${
                      row.balanceDueCents > 0n && isOverdue
                        ? "text-(--danger)"
                        : ""
                    }`}
                  >
                    {formatMoney(row.balanceDueCents, row.currency, {
                      locale: "en-BD",
                      display: "code",
                    })}
                  </span>
                </div>

                {/* Inline record payment */}
                {(row.status === "issued" || row.status === "partially_paid") &&
                row.balanceDueCents > 0n ? (
                  <div className="mt-3">
                    {!inlinePayOpen ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInlinePayOpen(true)}
                      >
                        <Plus className="h-3.5 w-3.5" /> Record payment
                      </Button>
                    ) : (
                      <div className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3">
                        <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                          Record payment
                        </div>
                        <PaymentForm
                          defaultAmountCents={row.balanceDueCents}
                          currency={row.currency}
                          maxAmountCents={row.balanceDueCents}
                          onSubmit={(values) => {
                            onInlinePayment(values);
                            setInlinePayOpen(false);
                          }}
                          onCancel={() => setInlinePayOpen(false)}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
              </section>

              {/* Notes */}
              {row.notes ? (
                <section>
                  <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                    Notes
                  </div>
                  <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                    {row.notes}
                  </p>
                </section>
              ) : null}

              {row.voidedAt && row.voidReason ? (
                <section>
                  <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                    Void reason
                  </div>
                  <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                    {row.voidReason}
                  </p>
                </section>
              ) : null}
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="invoices" recordId={row._id} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <AttachmentList
              parentTable="invoices"
              parentRecordId={row._id}
              purposeOptions={INVOICE_PURPOSE_OPTIONS}
              allowCustomPurpose
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop invoice docs here"
              description="Generated invoice PDF, delivery challan, signed copies, dealer PO."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <span className="text-[11px] text-(--text-tertiary)">
          Mock dataset · changes do not persist across reload.
        </span>
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Print portal — mounts the InvoicePrintView off-screen so the parent
 * component can clone it via `printElement`. The Tailwind utility classes
 * on the wrapper have no effect inside the print iframe (no Tailwind CSS
 * there), so the invoice renders at full width on paper.
 * ───────────────────────────────────────────────────────────── */

const PrintPortal = forwardRef<
  HTMLDivElement,
  {
    row: MockInvoice;
    lines: MockInvoiceLine[];
    payments: MockPayment[];
    dealer: MockDealerRecord | null;
  }
>(function PrintPortal({ row, lines, payments, dealer }, ref) {
  return (
    <div
      ref={ref}
      className="fixed inset-0 -z-50 opacity-0 pointer-events-none"
      aria-hidden="true"
    >
      <InvoicePrintView
        invoice={row}
        lines={lines}
        payments={payments}
        dealer={dealer}
      />
    </div>
  );
});

/* ─────────────────────────────────────────────────────────────
 * Field utility
 * ───────────────────────────────────────────────────────────── */

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
